import { Component, Output, EventEmitter, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { FieldService } from '../../../core/services/field.service';
import { DroneService } from '../../../core/services/drone.service';
import { OrganizationService, OrganizationMember } from '../../../core/services/organization.service';
import { SprayRecordService, CreateSprayRecordDto } from '../../../core/services/spray-record.service';
import { ToastService } from '../../../core/services/toast.service';
import { Field } from '../../../core/models/field.model';
import { Drone } from '../../../core/models/drone.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

/**
 * Quick Schedule Form Data
 */
export interface QuickScheduleFormData {
  applicationDate: string;
  fieldId: string;
  droneId: string;
  pilotId: string;
  notes: string;
}

/**
 * Quick Schedule Modal Component
 *
 * A modal for quickly creating a pending spray record from the dashboard.
 * Follows the standalone component pattern with signal-based state.
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */
@Component({
  selector: 'app-quick-schedule-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective, CharCounterDirective],
  templateUrl: './quick-schedule-modal.component.html',
  styleUrls: ['./quick-schedule-modal.component.scss']
})
export class QuickScheduleModalComponent implements OnInit {
  private router = inject(Router);
  private fieldService = inject(FieldService);
  private droneService = inject(DroneService);
  private organizationService = inject(OrganizationService);
  private sprayRecordService = inject(SprayRecordService);
  private toastService = inject(ToastService);

  @Output() scheduled = new EventEmitter<string>(); // Emits spray record ID
  @Output() cancelled = new EventEmitter<void>();

  // Modal state
  isOpen = signal(false);
  isProcessing = signal(false);
  saveAndContinue = signal(false);

  // Form data
  formData = signal<QuickScheduleFormData>({
    applicationDate: this.getTodayDate(),
    fieldId: '',
    droneId: '',
    pilotId: '',
    notes: ''
  });

  // Dropdown options
  fields = signal<Field[]>([]);
  drones = signal<Drone[]>([]);
  pilots = signal<OrganizationMember[]>([]);

  // Loading state for dropdowns
  loadingOptions = signal(false);

  // Validation errors
  errors = signal<Record<string, string>>({});

  ngOnInit(): void {
    // Dropdowns will be loaded when modal opens
  }

  /**
   * Open the modal and load dropdown options
   */
  async open(): Promise<void> {
    this.resetForm();
    this.isOpen.set(true);
    await this.loadDropdownOptions();
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.isProcessing()) return;
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
    }
  }

  /**
   * Submit the form (Save)
   */
  async onSave(): Promise<void> {
    this.saveAndContinue.set(false);
    await this.submit();
  }

  /**
   * Submit the form (Save & Continue Editing)
   */
  async onSaveAndContinue(): Promise<void> {
    this.saveAndContinue.set(true);
    await this.submit();
  }

  /**
   * Submit the form
   */
  private async submit(): Promise<void> {
    if (!this.validate()) return;

    this.isProcessing.set(true);

    try {
      const data = this.formData();

      // Build the CreateSprayRecordDto
      const dto: CreateSprayRecordDto = {
        fieldId: data.fieldId,
        applicationDate: data.applicationDate,
        status: 'pending',
        notes: data.notes || undefined,
        droneFlights: data.droneId ? [{
          droneId: data.droneId,
          pilotId: data.pilotId || undefined
        }] : []
      };

      const result = await this.sprayRecordService.createRecord(dto);

      if (!result) {
        throw new Error('Failed to create spray record');
      }

      // Emit the scheduled event
      this.scheduled.emit(result.id);

      if (this.saveAndContinue()) {
        // Redirect to edit page
        this.isOpen.set(false);
        this.router.navigate(['/spray-records', result.id, 'edit']);
      } else {
        // Stay on dashboard, show success toast
        this.toastService.success('Flight scheduled successfully');
        this.isOpen.set(false);
      }
    } catch (error: any) {
      console.error('[QuickScheduleModal] Error scheduling flight:', error);
      this.toastService.error(error.message || 'Failed to schedule flight');
    } finally {
      this.isProcessing.set(false);
    }
  }

  /**
   * Validate the form
   */
  private validate(): boolean {
    const data = this.formData();
    const newErrors: Record<string, string> = {};

    if (!data.applicationDate) {
      newErrors['applicationDate'] = 'Application date is required';
    }

    if (!data.fieldId) {
      newErrors['fieldId'] = 'Field is required';
    }

    this.errors.set(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  /**
   * Load dropdown options
   */
  private async loadDropdownOptions(): Promise<void> {
    this.loadingOptions.set(true);

    try {
      // Load options in parallel
      const [, , members] = await Promise.all([
        this.fieldService.loadFields(),
        this.droneService.loadOrganizationDrones(),
        this.organizationService.getMyOrganizationMembers()
      ]);

      // Update signals with loaded data
      this.fields.set(this.fieldService.getFieldOptions());
      this.drones.set(this.droneService.getDrones().filter(d => d.status === 'active'));
      this.pilots.set(members);
    } catch (error: any) {
      console.error('[QuickScheduleModal] Error loading options:', error);
      this.toastService.error('Failed to load form options');
    } finally {
      this.loadingOptions.set(false);
    }
  }

  /**
   * Reset the form to initial state
   */
  private resetForm(): void {
    this.formData.set({
      applicationDate: this.getTodayDate(),
      fieldId: '',
      droneId: '',
      pilotId: '',
      notes: ''
    });
    this.errors.set({});
    this.isProcessing.set(false);
    this.saveAndContinue.set(false);
  }

  /**
   * Get today's date in YYYY-MM-DD format
   */
  private getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  /**
   * Update form field
   */
  updateField(field: keyof QuickScheduleFormData, value: string): void {
    this.formData.update(data => ({
      ...data,
      [field]: value
    }));

    // Clear error for this field
    if (this.errors()[field]) {
      this.errors.update(errs => {
        const newErrors = { ...errs };
        delete newErrors[field];
        return newErrors;
      });
    }
  }

  /**
   * Check if field has error
   */
  hasError(field: string): boolean {
    return !!this.errors()[field];
  }

  /**
   * Get error message for field
   */
  getError(field: string): string {
    return this.errors()[field] || '';
  }
}
